import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { IconLoader, IconAlert, IconX } from "../components/Icons";
import SearchableSelect from "../components/SearchableSelect";
import { useCustomPaymentMethods, customPaymentName } from "../hooks/useCustomPaymentMethods";

interface CashOperation {
  id: string;
  employee_id: string | null;
  employee_name: string | null;
  type: string;
  amount: number;
  note: string | null;
  occurred_at: string;
}

interface CategorySale {
  category_id: string | null;
  name: string;
  amount: number;
}

interface PaymentSale {
  method: string;
  provider: string | null;
  amount: number;
}

interface TipByEmployee {
  employee_id: string;
  name: string | null;
  amount: number;
}

interface ShiftSummary {
  id: string;
  client_id: string | null;
  number: string | null;
  branch_id: string;
  branch_name: string | null;
  opened_by: string;
  opened_by_name: string | null;
  opened_at: string;
  closed_by: string | null;
  closed_by_name: string | null;
  closed_at: string | null;
  status: string;
  opening_cash: number;
  closing_cash: number | null;
  expected_cash: number | null;
  difference: number | null;
  currency: string;
  total_sales: number;
  excluded_sales: number;
  cash_in: number;
  cash_out: number;
  cash_operations: CashOperation[];
  categories: CategorySale[];
  payments: PaymentSale[];
  tips: TipByEmployee[];
}

interface Branch {
  id: string;
  name: string;
}

const STATUS_BADGES: Record<string, string> = {
  open: "badge badge-yellow",
  closed: "badge badge-green",
};

function formatMoney(minor: number, currency = "CZK"): string {
  return new Intl.NumberFormat("cs-CZ", { style: "currency", currency, minimumFractionDigits: 0 }).format(minor / 100);
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("cs-CZ");
}

export default function Settlements() {
  const { t } = useTranslation();
  const [shifts, setShifts] = useState<ShiftSummary[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterBranch, setFilterBranch] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selected, setSelected] = useState<ShiftSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (filterBranch !== "all") params.set("branch_id", filterBranch);
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (dateFrom) params.set("date_from", `${dateFrom}T00:00:00Z`);
      if (dateTo) params.set("date_to", `${dateTo}T23:59:59Z`);
      const [list, brs] = await Promise.all([
        api.get<ShiftSummary[]>(`/finance/shifts?${params}`).then((r) => r.data),
        api.get<Branch[]>("/branches?limit=500").then((r) => r.data).catch(() => [] as Branch[]),
      ]);
      setShifts(list);
      setBranches(brs);
    } catch (e) {
      setErr(apiError(e, t("settlements.loadError")));
    } finally {
      setLoading(false);
    }
  }, [filterBranch, filterStatus, dateFrom, dateTo, t]);

  const statusLabel = (s: string) => t(`settlements.status.${s}`, { defaultValue: s });
  const cashTypeLabel = (type: string) => t(`cashOps.type.${type}`, { defaultValue: type });
  const customMethods = useCustomPaymentMethods();
  const paymentLabel = (p: { method: string; provider?: string | null }) => {
    const key = p.method === "card" && p.provider === "sumup"
      ? "sumup"
      : p.provider === "meal_voucher" ? "meal_voucher" : p.method === "other" && p.provider ? customPaymentName(customMethods, p.provider) : p.method;
    return t(`orders.pay.${key}`, { defaultValue: key });
  };

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <IconLoader size={32} className="muted" />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t("settlements.title")}</h1>
        <p>{t("settlements.subtitle")}</p>
      </div>

      {err && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 20, background: "var(--red-dim)", color: "var(--red)", display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <IconAlert size={16} /> {err}
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          style={{ width: 160 }}
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          style={{ width: 160 }}
        />
        <div style={{ width: 200 }}>
          <SearchableSelect
            value={filterBranch}
            onChange={setFilterBranch}
            options={[{ value: "all", label: t("dashboard.allBranches") }, ...branches.map((b) => ({ value: b.id, label: b.name }))]}
            placeholder={t("dashboard.allBranches")}
            searchPlaceholder={t("dashboard.searchBranch")}
          />
        </div>
        <div style={{ width: 180 }}>
          <SearchableSelect
            value={filterStatus}
            onChange={setFilterStatus}
            options={[
              { value: "all", label: t("orders.allStatuses") },
              { value: "open", label: statusLabel("open") },
              { value: "closed", label: statusLabel("closed") },
            ]}
            placeholder={t("orders.allStatuses")}
            searchPlaceholder={t("orders.searchStatus")}
          />
        </div>
      </div>

      <div className="card">
        {shifts.length === 0 ? (
          <div style={{ color: "var(--muted)", padding: 20, textAlign: "center" }}>{t("settlements.empty")}</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t("settlements.colNumber")}</th>
                <th>{t("orders.colBranch")}</th>
                <th>{t("settlements.colOpened")}</th>
                <th>{t("settlements.colOpenedBy")}</th>
                <th>{t("settlements.colClosed")}</th>
                <th>{t("settlements.colClosedBy")}</th>
                <th>{t("settlements.openingCash")}</th>
                <th>{t("settlements.expectedCash")}</th>
                <th>{t("settlements.countedCash")}</th>
                <th>{t("settlements.difference")}</th>
                <th>{t("orders.colStatus")}</th>
              </tr>
            </thead>
            <tbody>
              {shifts.map((s) => (
                <tr key={s.id} style={{ cursor: "pointer" }} onClick={() => setSelected(s)}>
                  <td className="mono" style={{ fontWeight: 600 }}>{s.number || "—"}</td>
                  <td>{s.branch_name || "—"}</td>
                  <td>{formatDateTime(s.opened_at)}</td>
                  <td>{s.opened_by_name || "—"}</td>
                  <td>{formatDateTime(s.closed_at)}</td>
                  <td>{s.closed_by_name || "—"}</td>
                  <td>{formatMoney(s.opening_cash, s.currency)}</td>
                  <td>{s.expected_cash !== null ? formatMoney(s.expected_cash, s.currency) : "—"}</td>
                  <td>{s.closing_cash !== null ? formatMoney(s.closing_cash, s.currency) : "—"}</td>
                  <td style={{ fontWeight: 600, color: s.difference && s.difference !== 0 ? (s.difference < 0 ? "var(--red)" : "var(--green)") : undefined }}>
                    {s.difference !== null ? formatMoney(s.difference, s.currency) : "—"}
                  </td>
                  <td>
                    <span className={STATUS_BADGES[s.status] || "badge"}>
                      {statusLabel(s.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }} onClick={() => setSelected(null)}>
          <div className="card" style={{ width: "90%", maxWidth: 720, maxHeight: "90vh", overflow: "auto", padding: 24 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700 }}>{t("settlements.settlement")} {selected.number || "—"}</h3>
              <button className="ghost" onClick={() => setSelected(null)} style={{ padding: 4 }}><IconX size={20} /></button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, marginBottom: 20, fontSize: 14 }}>
              <div><span style={{ color: "var(--muted)" }}>{t("orders.colBranch")}:</span> {selected.branch_name || "—"}</div>
              <div><span style={{ color: "var(--muted)" }}>{t("settlements.colOpened")}:</span> {formatDateTime(selected.opened_at)}</div>
              <div><span style={{ color: "var(--muted)" }}>{t("settlements.colOpenedBy")}:</span> {selected.opened_by_name || "—"}</div>
              <div><span style={{ color: "var(--muted)" }}>{t("settlements.colClosed")}:</span> {formatDateTime(selected.closed_at)}</div>
              <div><span style={{ color: "var(--muted)" }}>{t("settlements.colClosedBy")}:</span> {selected.closed_by_name || "—"}</div>
            </div>

            <div className="card" style={{ padding: 16, marginBottom: 16, background: "var(--bg-elevated)" }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{t("settlements.cashSummary")}</h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, fontSize: 13 }}>
                <div>{t("settlements.openingCash")}: <strong>{formatMoney(selected.opening_cash, selected.currency)}</strong></div>
                <div>{t("settlements.expectedCash")}: <strong>{selected.expected_cash !== null ? formatMoney(selected.expected_cash, selected.currency) : "—"}</strong></div>
                <div>{t("settlements.countedCash")}: <strong>{selected.closing_cash !== null ? formatMoney(selected.closing_cash, selected.currency) : "—"}</strong></div>
                <div>{t("settlements.difference")}: <strong style={{ color: selected.difference && selected.difference !== 0 ? (selected.difference < 0 ? "var(--red)" : "var(--green)") : undefined }}>{selected.difference !== null ? formatMoney(selected.difference, selected.currency) : "—"}</strong></div>
                <div>{t("settlements.totalSales")}: <strong>{formatMoney(selected.total_sales, selected.currency)}</strong></div>
                <div>{t("settlements.excludedSales")}: <strong>{formatMoney(selected.excluded_sales, selected.currency)}</strong></div>
                <div>{t("cashOps.type.cash_in")}: <strong>{formatMoney(selected.cash_in, selected.currency)}</strong></div>
                <div>{t("cashOps.type.cash_out")}: <strong>{formatMoney(selected.cash_out, selected.currency)}</strong></div>
              </div>
            </div>

            {selected.cash_operations.length > 0 && (
              <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{t("cashOps.title")}</h4>
                <table>
                  <thead><tr><th>{t("settlements.colTime")}</th><th>{t("cashOps.colEmployee")}</th><th>{t("cashOps.colType")}</th><th>{t("cashOps.colAmount")}</th><th>{t("orders.colNote")}</th></tr></thead>
                  <tbody>
                    {selected.cash_operations.map((op) => (
                      <tr key={op.id}>
                        <td>{new Date(op.occurred_at).toLocaleString("cs-CZ")}</td>
                        <td>{op.employee_name || "—"}</td>
                        <td>{cashTypeLabel(op.type)}</td>
                        <td style={{ fontWeight: 600, color: op.amount < 0 ? "var(--red)" : undefined }}>{formatMoney(op.amount, selected.currency)}</td>
                        <td>{op.note || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {selected.payments.length > 0 && (
              <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{t("settlements.salesByPayment")}</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
                  {selected.payments.map((p, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>{paymentLabel(p)}</span>
                      <span style={{ fontWeight: 600 }}>{formatMoney(p.amount, selected.currency)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selected.categories.length > 0 && (
              <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{t("settlements.salesByCategory")}</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
                  {selected.categories.map((c, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>{c.name}</span>
                      <span style={{ fontWeight: 600 }}>{formatMoney(c.amount, selected.currency)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selected.tips.length > 0 && (
              <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>{t("orders.tip")}</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
                  {selected.tips.map((tip, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>{tip.name || "—"}</span>
                      <span style={{ fontWeight: 600 }}>{formatMoney(tip.amount, selected.currency)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
